import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, FileText, PenLine, Play, X } from "lucide-react";
import { COVER_IMAGE, EPOCHS, RETAILERS } from "@/lib/site";
import { scrollToId } from "@/lib/lenis";
import { EASE, Reveal, SectionHead } from "./shared";

export default function Libro() {
  const [showTrailer, setShowTrailer] = useState(false);

  return (
    <section id="libro" className="relative bg-lake-deep py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          kicker="Il Romanzo — Primo della Saga"
          title="Una pietra custodita dal drago. Sedici secoli di silenzio. Un amore che non doveva esistere."
        />

        <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal className="lg:sticky lg:top-28">
              <motion.div
                whileHover={{ rotateY: -8, rotateX: 4, y: -6 }}
                transition={{ type: "spring", stiffness: 120, damping: 14 }}
                className="relative mx-auto w-56 sm:w-64"
                style={{ transformStyle: "preserve-3d", perspective: 1000 }}
              >
                <img
                  src={COVER_IMAGE}
                  alt="Copertina de Il Segreto del Lago"
                  className="block w-full rounded-md border border-gold/40 shadow-[0_25px_50px_-15px_rgba(0,0,0,0.7)]"
                  data-testid="book-cover-image"
                />
              </motion.div>

              <div className="mt-10 space-y-3" data-testid="preorder-links">
                <p className="text-xs uppercase tracking-[0.25em] text-parchment-ivory/50">
                  Disponibile presso
                </p>
                {RETAILERS.map((r) => (
                  <a
                    key={r.id}
                    data-testid={`preorder-link-${r.id}`}
                    href={r.url}
                    target={r.url.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between rounded-lg border border-parchment-ivory/15 bg-lake-teal/30 px-5 py-3.5 transition-colors duration-300 hover:border-gold-shimmer/60 hover:bg-lake-teal/50"
                  >
                    <span className="font-medium text-parchment-ivory">{r.name}</span>
                    <ArrowUpRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                ))}
                <div className="mt-2 rounded-lg border border-gold/25 bg-gold/10 p-4" data-testid="signed-copy-note">
                  <p className="flex items-start gap-2.5 text-xs leading-relaxed text-parchment-ivory/80">
                    <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-gold-shimmer" />
                    <span>
                      <strong className="text-parchment-ivory">Copia con dedica e autografo?</strong>{" "}
                      Scrivi una email a{" "}
                      <a
                        href="mailto:autrice@ilsegretodellago.com?subject=Copia%20con%20dedica%20e%20autografo%20—%20Il%20Segreto%20del%20Lago"
                        className="text-gold-shimmer underline decoration-gold/40 underline-offset-2 hover:decoration-gold-shimmer"
                        data-testid="signed-copy-email-link"
                      >
                        autrice@ilsegretodellago.com
                      </a>{" "}
                      oppure ritirala di persona agli eventi:{" "}
                      <button
                        onClick={() => scrollToId("eventi")}
                        className="text-gold-shimmer underline decoration-gold/40 underline-offset-2 hover:decoration-gold-shimmer"
                        data-testid="signed-copy-events-link"
                      >
                        le prossime date sono nella sezione Eventi
                      </button>
                      .
                    </span>
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-serif text-lg leading-relaxed text-parchment-ivory/90 sm:text-xl" data-testid="book-synopsis">
                C'è un lago che custodisce segreti più profondi delle sue acque.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-parchment-ivory/70">
                Un santo, un drago, una pietra dal potere sconosciuto. Una ragazza che sfida il
                destino per amore. Un uomo che annega la sua disperazione nelle stesse acque.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-parchment-ivory/70">
                Secoli separano le loro storie. Ma il lago non dimentica.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-parchment-ivory/70">
                Quando un fulmine squarcia la notte e risveglia ciò che era stato sepolto, passato
                e presente si scontrano. Marco torna alla vita, ma non è più lo stesso. E tra le
                rive del Lago d'Orta, qualcuno lo stava aspettando da molto più tempo di quanto
                immagini.
              </p>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap gap-3" data-testid="book-epochs">
                {EPOCHS.map((e, i) => (
                  <div
                    key={e.era}
                    className="flex items-center gap-3 rounded-full border border-gold/25 bg-lake-teal/30 py-2 pl-4 pr-5"
                  >
                    <span className="font-display text-xs text-gold">{["I", "II", "III", "IV"][i]}</span>
                    <span className="text-xs text-parchment-ivory/80">
                      <span className="font-semibold text-gold-shimmer">{e.era}</span> — {e.event}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <blockquote className="mt-10 border-l-2 border-gold pl-6 font-serif text-xl italic text-gold-shimmer/90">
                «Un segreto sepolto. Un amore impossibile. Un'unica verità pronta a riemergere.»
              </blockquote>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Reveal delay={0.1}>
                <a
                  data-testid="book-excerpt-download-button"
                  href="/assets/estratto-primo-capitolo.pdf"
                  download
                  className="flex w-full items-center justify-between rounded-lg border border-gold/50 bg-lake-teal/40 px-5 py-4 text-left shadow-[0_0_20px_rgba(197,155,39,0.1)] transition-colors duration-300 hover:border-gold-shimmer hover:bg-lake-teal/60"
                >
                  <span className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-gold" />
                    <span>
                      <span className="block text-sm font-semibold text-parchment-ivory">
                        Scarica l'estratto PDF
                      </span>
                      <span className="block text-xs text-parchment-ivory/50">
                        Il primo capitolo: «La fuga» — Egina, Grecia, IV secolo
                      </span>
                    </span>
                  </span>
                  <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink-dark">
                    Scarica
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.2}>
                <button
                  data-testid="book-trailer-button"
                  onClick={() => setShowTrailer(true)}
                  className="flex w-full items-center justify-between rounded-lg border border-gold/50 bg-lake-teal/40 px-5 py-4 text-left shadow-[0_0_20px_rgba(197,155,39,0.1)] transition-colors duration-300 hover:border-gold-shimmer hover:bg-lake-teal/60"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15">
                      <Play className="h-4 w-4 text-gold-shimmer" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-parchment-ivory">
                        Guarda il booktrailer
                      </span>
                      <span className="block text-xs text-parchment-ivory/50">
                        Il romanzo in 50 secondi, tra le quattro epoche
                      </span>
                    </span>
                  </span>
                  <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink-dark">
                    Guarda
                  </span>
                </button>
              </Reveal>
            </div>

            <Reveal delay={0.3}>
              <p className="mt-8 text-sm text-parchment-ivory/50">
                In libreria e online da <span className="text-gold-shimmer">novembre 2026</span>, in
                edizione cartacea — la versione digitale arriverà in seguito. Il secondo
                volume è già in scrittura.
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showTrailer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-lake-deep/95 p-4 backdrop-blur-md"
            onClick={() => setShowTrailer(false)}
            data-testid="trailer-modal"
          >
            <button
              data-testid="trailer-close"
              onClick={() => setShowTrailer(false)}
              aria-label="Chiudi il booktrailer"
              className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-parchment-ivory transition-colors hover:bg-gold/15"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                data-testid="trailer-video"
                src="/assets/booktrailer.mp4"
                controls
                autoPlay
                playsInline
                className="w-full rounded-lg border border-gold/30 shadow-2xl"
              />
              <p className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-parchment-ivory/50">
                Il Segreto del Lago · Booktrailer ufficiale
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
